import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProducts, getStats } from "@/lib/api";
import type { Product } from "@/lib/types";

const quickActions = [
  { label: 'Nuevo Producto', icon: 'ri-add-circle-line', link: '/admin/productos', color: 'bg-[#00b4c8] text-white hover:bg-[#0099aa]' },
  { label: 'Favoritos del Mes', icon: 'ri-star-line', link: '/admin/favoritos', color: 'bg-[#fde68a] text-[#2d1a0e] hover:bg-[#fcd34d]' },
  { label: 'Nueva Promoción', icon: 'ri-price-tag-3-line', link: '/admin/promociones', color: 'bg-[#c9b8f0] text-[#2d1a0e] hover:bg-[#b8a0e8]' },
  { label: 'Nuevo Artículo', icon: 'ri-article-line', link: '/admin/blog', color: 'bg-[#d4607a] text-white hover:bg-[#b84d65]' },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    activeProducts: 0,
    promotions: 0,
    blogArticles: 0,
    categories: 0,
  });
  const [recentProducts, setRecentProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);

        const [statsData, products] = await Promise.all([
          getStats(),
          getProducts(),
        ]);

        setStats(statsData);

        const recent = [...products]
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          )
          .slice(0, 5);

        setRecentProducts(recent);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const statCards = [
    { label: 'Productos Activos', value: stats.activeProducts, icon: 'ri-cake-line', color: 'bg-[#00b4c8]/10 text-[#00b4c8]', link: '/admin/productos' },
    { label: 'Promociones', value: stats.promotions, icon: 'ri-price-tag-3-line', color: 'bg-[#fde68a]/50 text-[#b08000]', link: '/admin/promociones' },
    { label: 'Artículos del Blog', value: stats.blogArticles, icon: 'ri-article-line', color: 'bg-[#c9b8f0]/30 text-[#7c5cbf]', link: '/admin/blog' },
    { label: 'Categorías', value: stats.categories, icon: 'ri-folder-line', color: 'bg-[#f9b8c8]/40 text-[#d4607a]', link: '/admin/productos' },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#7c5cbf] via-[#00b4c8] to-[#d4607a] rounded-2xl p-6 text-white">
        <h1 className="text-xl font-bold mb-1">
          Bienvenido al Panel de Administración
        </h1>
        <p className="text-white/80 text-sm">
          Gestiona productos, promociones y publicaciones del blog de San7o Pecado.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Link
            key={stat.label}
            to={stat.link}
            className="bg-white rounded-xl p-5 border border-[#c9b8f0]/20 hover:border-[#00b4c8]/40 transition-colors cursor-pointer group"
          >
            <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${stat.color} mb-3`}>
              <i className={`${stat.icon} text-lg`}></i>
            </div>
            {loading ? (
              <div className="h-8 w-12 bg-[#f0e6d8] rounded animate-pulse mb-1"></div>
            ) : (
              <p className="text-2xl font-bold text-[#2d1a0e] group-hover:text-[#00b4c8] transition-colors">
                {stat.value}
              </p>
            )}
            <p className="text-xs text-[#6b5b6e] mt-0.5">{stat.label}</p>
          </Link>
        ))}
      </div>

      {/* Quick actions */}
      <div>
        <h3 className="text-sm font-semibold text-[#7c5cbf] uppercase tracking-wider mb-3">
          Acciones Rápidas
        </h3>
        <div className="flex flex-wrap gap-3">
          {quickActions.map((action) => (
            <Link
              key={action.label}
              to={action.link}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${action.color}`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${action.icon} text-sm`}></i>
              </span>
              {action.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Recent products */}
      <div className="bg-white rounded-xl border border-[#c9b8f0]/20 overflow-hidden">
        <div className="px-5 py-4 border-b border-[#c9b8f0]/20 flex items-center justify-between">
          <h3 className="font-semibold text-[#2d1a0e] text-sm">Productos Recientes</h3>
          <Link to="/admin/productos" className="text-xs text-[#00b4c8] hover:underline cursor-pointer">
            Ver todos →
          </Link>
        </div>
        <div className="divide-y divide-[#c9b8f0]/15">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-3">
                <div className="w-10 h-10 rounded-lg bg-[#f0e6d8] animate-pulse flex-shrink-0"></div>
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-[#f0e6d8] rounded animate-pulse w-3/4"></div>
                  <div className="h-2.5 bg-[#f0e6d8] rounded animate-pulse w-1/2"></div>
                </div>
              </div>
            ))
          ) : (
            recentProducts.map((product) => (
              <div key={product.id} className="flex items-center gap-4 px-5 py-3">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-10 h-10 rounded-lg object-cover object-top flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2d1a0e] truncate">{product.name}</p>
                  <p className="text-xs text-[#6b5b6e]">{product.category}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-[#d4607a]">${product.price}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${product.active ? 'bg-[#00b4c8]/10 text-[#00b4c8]' : 'bg-gray-100 text-gray-500'}`}>
                    {product.active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
